import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAudioPlayer } from "../../hooks/useAudioPlayer";
import { useHapticEngine } from "../../hooks/useHapticsEngine";
import { useScrollLock } from "../../hooks/useScrollLock";
import { useLocalFilesStore } from "../../stores/localFilesStore";
import { useLocalLibraryStore } from "../../stores/localLibraryStore";
import { usePlayerStore } from "../../stores/playerStore";
import { useRightSidebarStore } from "../../stores/rightSidebarStore";
import { useStatsStore } from "../../stores/statsStore";
import { getDefaultAlbumArt } from "../../utils/defaults";
import { ExpandedPlayer } from "./ExpandedPlayer";
import { HapticPresetSelector } from "./HapticPresetSelector";
import { MiniPlayer } from "./MiniPlayer";
import { WindowedPlayer } from "./WindowedPlayer";

declare global {
  interface Window {
    updateTitle: (trackName: string, artistName: string) => void;
    resetTitle: () => void;
  }
}

export const Player = () => {
  const location = useLocation();
  const {
    currentTrack,
    isPlaying,
    progress,
    volume,
    mode,
    loopMode,
    setVolume,
    togglePlay,
    setMode,
    setLoopMode,
    playNext,
    playPrevious,
  } = usePlayerStore();

  const {
    analyzeTrack,
    settings: hapticsSettings,
    toggleEnabled: toggleHaptics,
    updateSettings: updateHapticsSettings,
    isSupported: isHapticsSupported,
  } = useHapticEngine(null); // Pass null initially

  const { getFileUrl } = useLocalFilesStore();
  const { isContentLiked, toggleLike } = useLocalLibraryStore();
  const { seekByPercentage } = useAudioPlayer();
  const { addPlayEvent } = useStatsStore();
  const { openSidebar } = useRightSidebarStore();

  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState<number | null>(null);
  const [showHapticPresets, setShowHapticPresets] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const playStartTimeRef = useRef<number | null>(null);

  useScrollLock(mode === "expanded" || mode === "fullscreen");

  // Update title when currentTrack changes
  useEffect(() => {
    if (currentTrack && window.updateTitle && isPlaying) {
      window.updateTitle(currentTrack.title || "Unknown Title", currentTrack.artist || "Unknown Artist");
    }
    if (currentTrack && window.resetTitle && !isPlaying) {
      window.resetTitle();
    }
  }, [currentTrack, isPlaying]);

  // Collapse player to mini when route changes
  useEffect(() => {
    if (mode !== "mini") {
      setMode("mini");
    }
  }, [location, setMode]);

  // Track play events for stats
  useEffect(() => {
    if (isPlaying && currentTrack) {
      playStartTimeRef.current = Date.now();
    } else if (!isPlaying && playStartTimeRef.current && currentTrack) {
      const duration = (Date.now() - playStartTimeRef.current) / 1000; // Convert to seconds
      if (duration > 0) {
        // Only log valid durations
        addPlayEvent(currentTrack, duration, false);
      }
      playStartTimeRef.current = null;
    }
  }, [isPlaying, currentTrack, addPlayEvent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playStartTimeRef.current && currentTrack) {
        const duration = (Date.now() - playStartTimeRef.current) / 1000;
        if (duration > 0) {
          addPlayEvent(currentTrack, duration, false);
        }
      }
    };
  }, [currentTrack, addPlayEvent]);

  // Track skips for stats
  const handleNext = () => {
    if (currentTrack && playStartTimeRef.current) {
      const duration = (Date.now() - playStartTimeRef.current) / 1000;
      if (duration > 0) {
        addPlayEvent(currentTrack, duration, true);
      }
      playStartTimeRef.current = null;
    }
    playNext();
  };

  const handlePrevious = () => {
    if (currentTrack && playStartTimeRef.current) {
      const duration = (Date.now() - playStartTimeRef.current) / 1000;
      if (duration > 0) {
        addPlayEvent(currentTrack, duration, true);
      }
      playStartTimeRef.current = null;
    }
    playPrevious();
  };

  const handleOpenMenu = () => {
    setShowMenu(true);
  };

  const handleAddToPlaylist = () => {
    openSidebar("addToPlaylist");
  };

  const handleAddToAlbum = () => {
    openSidebar("addToAlbum");
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
  };

  const handleCloseHapticPresets = () => {
    setShowHapticPresets(false);
  };

  const handleHapticPresetOpen = () => {
    setShowHapticPresets(true);
  };

  if (!currentTrack) return null;

  const albumArt = currentTrack.albumArt || getDefaultAlbumArt(currentTrack.source);
  const artist = currentTrack.artist || "Unknown Artist";
  const title = currentTrack.title || "Unknown Title";

  const handleSeekStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    setDragProgress(progress);
  };

  const handleSeek = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;

    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const percentage = ((clientX - rect.left) / rect.width) * 100;
    setDragProgress(Math.max(0, Math.min(100, percentage)));
  };

  const handleSeekEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (dragProgress !== null) {
      seekByPercentage(dragProgress);
    }
    setIsDragging(false);
    setDragProgress(null);
  };

  const handleHapticsClick = () => {
    if (!hapticsSettings.enabled) {
      setShowHapticPresets(true);
    } else {
      toggleHaptics();
    }
  };

  // Update haptic preset selection handler
  const handleHapticPresetSelect = async (preset: string) => {
    console.log("=== Player: Selecting haptic preset:", preset);

    if (!currentTrack) {
      console.error("=== Player: No current track");
      return;
    }

    let blobData: ArrayBuffer;
    try {
      if (currentTrack.blobData instanceof ArrayBuffer) {
        blobData = currentTrack.blobData;
      } else if (currentTrack.source === "local") {
        // Fetch blob data if not already available
        const response = await fetch(currentTrack.url);
        blobData = await response.arrayBuffer();
      } else {
        console.error("=== Player: Track source not supported for haptics");
        return;
      }

      const tempContext = new AudioContext();
      const audioBuffer = await tempContext.decodeAudioData(blobData.slice(0));

      console.log("=== Player: Starting analysis with preset:", preset);
      const analysis = await analyzeTrack(audioBuffer, currentTrack.id, preset);

      if (analysis) {
        console.log("=== Player: Analysis successful, updating settings");
        updateHapticsSettings({ currentPreset: preset });
        if (!hapticsSettings.enabled) {
          toggleHaptics();
        }
      }

      await tempContext.close();
    } catch (error) {
      console.error("=== Player: Failed to analyze track for haptics:", error);
    }

    setShowHapticPresets(false);
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: currentTrack.title,
        text: `Check out ${currentTrack.title} by ${currentTrack.artist}`,
        url: window.location.href,
      });
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  const renderContent = () => {
    switch (mode) {
      case "windowed":
        return <WindowedPlayer />;
      case "expanded":
      case "fullscreen":
        return (
          <motion.div
            layout
            className="relative bg-gradient-to-b from-zinc-900 to-black h-full w-full md:w-[calc(100%-(21px))] md:ml-[14px] my-0 md:my-[--outer-margin] overflow-hidden rounded-lg h-[calc(100vh-60px)] md:h-[calc(100vh-14px)]"
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.8), rgb(0,0,0)), url(${albumArt})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <ExpandedPlayer
              title={title}
              artist={artist}
              albumArt={albumArt}
              isPlaying={isPlaying}
              progress={progress}
              volume={volume}
              duration={currentTrack.duration}
              loopMode={loopMode}
              isLiked={isContentLiked(currentTrack.id, "track")}
              isHapticsSupported={isHapticsSupported}
              isHapticsEnabled={hapticsSettings.enabled}
              hideHeader={mode === "fullscreen"}
              isDragging={isDragging}
              dragProgress={dragProgress}
              onMinimize={() => setMode("mini")}
              onPlayPause={togglePlay}
              onNext={handleNext}
              onPrevious={handlePrevious}
              onLoopChange={setLoopMode}
              onVolumeChange={handleVolumeChange}
              onLikeToggle={() => toggleLike(currentTrack.id, "track", currentTrack.source)}
              onShare={handleShare}
              onHapticsToggle={handleHapticsClick}
              onSeekStart={handleSeekStart}
              onSeek={handleSeek}
              onSeekEnd={handleSeekEnd}
              onAddToPlaylist={handleAddToPlaylist}
              onAddToAlbum={handleAddToAlbum}
              showHapticPresets={showHapticPresets}
              trackId={currentTrack.id}
            />
            <AnimatePresence>
              {showHapticPresets && (
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                  className="absolute bottom-0 left-0 right-0 z-20"
                >
                  <div className="bg-gradient-to-t from-black to-transparent h-24 absolute bottom-full left-0 right-0" />
                  <HapticPresetSelector
                    onSelect={handleHapticPresetSelect}
                    onClose={handleCloseHapticPresets}
                    currentPreset={hapticsSettings.currentPreset || "pop"}
                    onEnable={toggleHaptics}
                    isEnabled={hapticsSettings.enabled}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      default:
        return (
          <motion.div
            layout
            className="bg-black border-t border-zinc-800 h-full w-full md:w-[calc(100%-21px)] ml-auto md:mb-[--outer-margin] md:mr-[--outer-margin] md:rounded-b-lg"
          >
            <MiniPlayer
              title={title}
              artist={artist}
              albumArt={albumArt}
              isPlaying={isPlaying}
              progress={progress}
              duration={currentTrack.duration}
              isDragging={isDragging}
              dragProgress={dragProgress}
              isLiked={isContentLiked(currentTrack.id, "track")}
              isHapticsSupported={isHapticsSupported}
              isHapticsEnabled={hapticsSettings.enabled}
              onPlayPause={togglePlay}
              onExpand={() => setMode("expanded")}
              onWindowedMode={() => setMode("windowed")}
              onSeekStart={handleSeekStart}
              onSeek={handleSeek}
              onSeekEnd={handleSeekEnd}
              onLikeToggle={() => toggleLike(currentTrack.id, "track", currentTrack.source)}
              onShare={handleShare}
              onHapticsToggle={handleHapticsClick}
              onOpenMenu={handleOpenMenu}
              onHapticPresetOpen={handleHapticPresetOpen}
              trackId={currentTrack.id}
            />
            {showHapticPresets && (
              <HapticPresetSelector
                onSelect={handleHapticPresetSelect}
                onClose={handleCloseHapticPresets}
                currentPreset={hapticsSettings.currentPreset || "pop"} // Set default preset
                onEnable={toggleHaptics}
                isEnabled={hapticsSettings.enabled}
              />
            )}
          </motion.div>
        );
    }
  };

  return (
    <motion.div
      layout
      className={`fixed ${
        mode === "mini"
          ? "bottom-[60px] md:bottom-0 left-0 md:left-72 right-0"
          : "inset-0 md:left-72 bottom-[60px] md:bottom-0"
      } z-50 ${mode === "fullscreen" ? "top-[60px]" : ""}`}
    >
      <AnimatePresence mode="wait">{renderContent()}</AnimatePresence>
    </motion.div>
  );
};
